import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import AppManager from "../../managers/AppManager";
import RouteManager, { PAGE_ROUTES } from "../../managers/RouteManager";
import { useTranslation } from "react-i18next";
import PerformaceService from "../../services/PerformaceService";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKIcon,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    DKLine,
    showToast,
    TOAST_TYPE,
    showLoader,
    removeLoader,
    showAlert
} from "deskera-ui-library";
import ic_user from "../../assets/icons/ic_user.png";
import Utility, { deepClone, removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import { renderGoal } from "./PerformanceConstant";
import { TENANT, TenantService } from "../../managers/TenantManagerService";

function AdminReview(props) {
    const history = useHistory();
    const { t } = useTranslation();

    const TABS = [
        { name: t("EVALUATION"), value: "evaluation" },
        { name: t("EMP_SELF_ASSESSMENT"), value: "employeeSelfAssessment" }
    ]

    const initialOverallCommentState = {
        id: "appraiserOverallComment",
        title: "",
        placeholder: t("COMMENT_PLACEHOLDER"),
        type: INPUT_TYPE.LONG_TEXT,
        showValueLength: true,
        validation: [["required"], ["maxLength", 500]]
    };

    const [assessment, setAssessment] = useState(null);
    const [appraiserOverallComment, setAppraiserOverallComment] = useState(initialOverallCommentState);
    const [assessmentSubmitted, setAssessmentSubmitted] = useState(true);
    const [appraiserAssessments, setAppraiserAssessments] = useState([]);
    const [employeeAssessments, setEmployeeAssessments] = useState([]);
    const [activeTab, setActiveTab] = useState(TABS[0].value);
    const [ratings, setRatings] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [updateAverageRating, setUpdateAverageRating] = useState(false);
    const [evaluationGoal, setEvaluationGoal] = useState([]);
    const [employeeGoal, setEmployeeGoal] = useState([]);

    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();

        if (PerformaceService.ASSESSMENT) {
            setAssessment(PerformaceService.ASSESSMENT);
            PerformaceService.ASSESSMENT = null;
        } else {
            RouteManager.navigateToPage(PAGE_ROUTES.ASSESSMENT_REVIEW);
        }
    }, []);

    useEffect(() => {
        if (assessment) {
            getRatings();
            getAppraiserAssessment();
            getSelfAssessment();
        }
    }, [assessment]);

    useEffect(() => {
        if (appraiserAssessments?.length > 0 && updateAverageRating) {
            calcuateAverageRating();
            setUpdateAverageRating(false);
        }
    }, [appraiserAssessments]);

    const getRatings = () => {
        PerformaceService.getRatings().then(
            (response) => {
                if (response?.length > 0) {
                    setRatings(response);
                }
            }, (error) => {
                setRatings([]);
            }
        );
    }

    const getSelfAssessment = () => {
        const params = {
            appraisalId: assessment.appraisalId.id,
            appraiseeId: assessment.appraisee?.iamUserId
        }

        PerformaceService.getSelfAssessment(params).then(
            (response) => {
                if (response?.questions?.length > 0) {
                    setAssessments(response, setEmployeeAssessments);
                    setEmployeeGoal(response.goalSubmissions || []);
                }
            }, (error) => {}
        );
    }

    const getAssessmentField = (questionId, comment) => {
        return {
            id: questionId,
            title: "",
            placeholder: t("COMMENT_PLACEHOLDER"),
            type: INPUT_TYPE.LONG_TEXT,
            showValueLength: true,
            validation: [["required"], ["maxLength", 500]],
            value: comment
        }
    }

    const setAssessments = (response, setState) => {
        const assessments = [];
        const sortedQuestions = response.questions.sort((q1, q2) => q1.seq - q2.seq);
        sortedQuestions.forEach((question) => {
            const assessment = {
                seq: question.seq,
                questionId: question.id,
                question: question.question,
                ...getAssessmentComment(response.assessments, question.id)
            };

            assessments.push({
                ...assessment,
                ...getAssessmentField(assessment.questionId, assessment.comment)
            });
        });

        setState(assessments);
    }

    const getAssessmentComment = (assessment, questionId) => {
        if (assessment?.length > 0) {
            const comment = assessment.find((assessment) => assessment.questionId === questionId);
            if(comment) {
                return {
                    comment: comment.comment,
                    rating: comment.rating
                };
            } else {
                return {};
            }
        }

        return {};
    }

    const getAppraiserAssessment = () => {
        if(Utility.isEmpty(assessment.appraisalId)) {
            return
        }
        const params = {
            appraisalId: assessment.appraisalId.id,
            appraiseeId: assessment.appraisee?.iamUserId
        }

        const timeout = showDelayedLoader();
        PerformaceService.getAppraiserAssessment(params).then(
            (response) => {
                removeDelayedLoader(timeout);
                if (response?.questions?.length > 0) {
                    setUpdateAverageRating(true);
                    setAssessments(response, setAppraiserAssessments);
                    setAssessmentSubmitted(response.assessmentSubmitted);
                    setEvaluationGoal(response.goalSubmissions || []);
                    if (response.appraiserOverallComment) {
                        updateState("appraiserOverallComment", setAppraiserOverallComment, appraiserOverallComment, {value: response.appraiserOverallComment});
                    }
                }
            }, (error) => {
                removeDelayedLoader(timeout);
            }
        );
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <DKLabel
                    className="p-h-xl ml-xs fs-s text-red"
                    text={errorMessage}
                />
            )
        }
    }

    const getMaxLengthValue = (validation) => {
        return validation ? validation.find((v) => v[0] === "maxLength")?.[1] : false;
    }

    const showValueLength = (field) => {
        return (
            <DKLabel
                className="p-h-xl ml-auto"
                text={`${field.value?.length || 0}/${getMaxLengthValue(field.validation)}`}
            />
        );
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "maxLength":
                        if (value?.length > validation[1]) {
                            error.invalid = true;
                            error.errorMessage = t("MAXLENGTH_EXCEEDS");
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, setState, state, value) => {
        let newState;

        switch (stateName) {
            case "appraiserAssessments":
            case "employeeAssessments":
                if (stateName === "appraiserAssessments") {
                    newState = [...appraiserAssessments];
                } else {
                    newState = [...employeeAssessments];
                }

                if (Array.isArray(newState)) {
                    newState = newState.map((q) => {
                        if (q.id === state.id) {
                            q = {...q, ...value};

                            if (Object.keys(value)[0] === "rating") {
                                if (hasError(q, q.rating).invalid) {
                                    q.invalidRating = true;
                                } else {
                                    q.invalidRating = false;
                                }
                            } else {
                                q = {...q, ...hasError(q, q.value)};
                            }
                        }

                        return q;
                    });
                }
                break;
            default:
                newState = {...state, ...value};
                newState = {...newState, ...hasError(newState, newState.value)};
                break;
        }

        if (Object.keys(value)[0] === "rating") {
            setUpdateAverageRating(true);
        }

        setState(newState);
    }

    const calcuateAverageRating = () => {
        let total = 0;
        appraiserAssessments.forEach((assessment) => {
            if (assessment.rating) {
                total += parseInt(assessment.rating);
            }
        });

        const averageRating = total / appraiserAssessments.length;
        setAverageRating(!isNaN(averageRating) ? averageRating.toFixed(1) : 0);
    }

    const isInvalid = () => {
        let invalid = false;

        const newState = {...appraiserOverallComment, ...hasError(appraiserOverallComment, appraiserOverallComment.value)};
        if (newState.invalid) {
            invalid = true;
        }

        setAppraiserOverallComment(newState);

        if (Array.isArray(appraiserAssessments)) {
            const newState = [...appraiserAssessments].map((question) => {
                const newQuestion = {...question, ...hasError(question, question.value)};
                if (newQuestion.invalid) {
                    invalid = true;
                }

                if (Utility.isEmpty(newQuestion.rating)) {
                    invalid = true;
                    newQuestion.invalidRating = true;
                }

                return newQuestion;
            });

            setAppraiserAssessments(newState);
        }

        return invalid;
    }

    const getPayload = (isDraft) => {
        const assessments = appraiserAssessments.map((assessment) => {
            return {
                questionId: assessment.questionId,
                comment: assessment.value || "",
                rating: String(assessment.rating || "")
            }
        });

        const payload = {
            appraisalId: assessment.appraisalId.id,
            appraiseeId: assessment.appraisee?.iamUserId,
            appraiserOverallComment: appraiserOverallComment.value,
            assessmentSubmitted: !isDraft,
            assessments: assessments,
            goalSubmissions: evaluationGoal
        }

        return payload;
    }

    const showConfirmationMessage = () => {
        if (isInvalid()) {
            return;
        }

        showAlert(t("ALERT"), t("CONFIRMATION_MSG"), [
            {
                title: t("YES"),
                className: "bg-app text-white mr-xl",
                onClick: () => {
                    saveAssessmentReview(false);
                }
            },
            {
                title: t("NO"),
                className: "bg-gray1 border-m"
            }
        ]);
    }

    const saveAssessmentReview = (isDraft) => {
        showLoader(t("SAVING_APPRAISER_REVIEW"));
        const payload = getPayload(isDraft);
        PerformaceService.saveAppraiserAssessment(payload).then(
            (response) => {
                removeLoader();
                const msg = isDraft ? "APPRAISER_REVIEW_DRAFT_SUCCESS" : "APPRAISER_REVIEW_SAVE_SUCCESS";
                showToast(t(msg), TOAST_TYPE.SUCCESS);
                setAssessmentSubmitted(!isDraft);
            }, (error) => {
                removeLoader();
                showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
            }
        );
    }

    const getDisableCSS = (initial, isButton) => {
        let CSSClass = initial || "";

        if (assessmentSubmitted) {
            CSSClass += isButton ? " disable-button" : " disable-section";
        }

        return CSSClass;
    }

    const renderField = (stateName, setState, field, width) => {
        switch (field.type) {
            case INPUT_TYPE.LONG_TEXT:
                return (
                    <div key={field.id} style={{ flex: "0 0 50%", maxWidth: width }}>
                        <div className="row pb-xl p-h-xl position-relative">
                            <DKInput
                                title={field.title}
                                type={field.type}
                                placeholder={field.placeholder}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || assessmentSubmitted}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, setState, field, {value: value});
                                }}
                            />
                        </div>
                        <div className="row" style={{ marginTop: "-20px", marginBottom: "9px" }}>
                            {field.invalid && showError(field.errorMessage)}
                            {field.showValueLength && showValueLength(field)}
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    const getTabs = () => {
        const defaultCls = "p-h-xl p-v-m text-align-center cursor-hand";
        const tabView = [];

        TABS.forEach((tab) => {
            tabView.push(
                <div
                    className={`${defaultCls} ${activeTab === tab.value ? "fw-m active-tab-border text-app" : "text-dark-gray"}`}
                    onClick={() => {
                        setActiveTab(tab.value);
                    }}
                >
                    {tab.name}
                </div>
            );
        });

        return (
            <div className="row">
                {tabView}
            </div>
        );
    };

    const renderRatings = (assessment, isSelfAssessment) => {
        const defaultCls = `column align-items-center p-xl rating ${isSelfAssessment || assessmentSubmitted ? "rating-disabled" : "cursor-hand"}`;

        return (
            <div className="row p-h-xl m-v-xl">
                <div className={`d-flex ${assessment.invalidRating ? "rating-invalid" : "border-m"} `}>
                    {ratings.map((rating, index) => {
                        return (
                            <div
                                className={`${!isSelfAssessment && !assessmentSubmitted ? "bg-light-app-hover":""}  ${defaultCls} ${assessment.rating === rating.rating ? "rating-selected" : ""}`}
                                style={{
                                    borderRight: index !== ratings.length - 1 ? "1px solid #dcdcdc" : ""
                                }}
                                onClick={() => {
                                    if (!isSelfAssessment && !assessmentSubmitted) {
                                        updateState("appraiserAssessments", setAppraiserAssessments, assessment, { rating: rating.rating});
                                    }
                                }}
                            >
                                <DKLabel text={rating.rating} className="fw-b mb-s" />
                                <DKLabel text={rating.description} className="text-align-center" />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    const renderAssessmentQuestions = (assessment, index, isSelfAssessment) => {
        return (
            <div>
                {index !== 0 && <DKLine className="bg-gray3 mb-xl" />}
                <DKLabel text={`${t("QUESTION")} ${index + 1}:`} className="text-uppercase text-gray p-h-xl" />
                <DKLabel text={assessment.question} className="fw-b fs-m mt-m p-h-xl" />
                {!isSelfAssessment && (
                    <div className="mt-m">
                        {renderField("appraiserAssessments", setAppraiserAssessments, assessment, "90%")}
                    </div>
                )}
                {isSelfAssessment && (
                    <div className="p-h-xl m-v-m" style={{ flex: "0 0 50%", maxWidth: "90%" }}>
                        <div className="p-s border-radius-s bg-gray1" style={{ height: "75px" }}>
                            {assessment.comment}
                        </div>
                    </div>
                )}
                {renderRatings(assessment, isSelfAssessment)}
            </div>
        );
    }

    const renderActiveTab = () => {
        let assessments;
        let isSelfAssessment = false;

        switch (activeTab) {
            case "evaluation":
                assessments = appraiserAssessments;
                isSelfAssessment = false;
                break;
            case "employeeSelfAssessment":
                assessments = employeeAssessments;
                isSelfAssessment = true;
                break;
            default:
                assessments = [];
                break;
        }

        return (
            <div>
                {assessments.map((assessment, index) => renderAssessmentQuestions(assessment, index, isSelfAssessment))}
            </div>
        );
    }

    const renderGoalSection = () => {
        let goalSubmissions = [];
        let isSelfAssessment = false;
        switch (activeTab) {
            case "evaluation":
                goalSubmissions = evaluationGoal;
                isSelfAssessment = assessmentSubmitted;
                break;
            case "employeeSelfAssessment":
                goalSubmissions = employeeGoal;
                isSelfAssessment = true;
                break;
            default:
                goalSubmissions = [];
                break;
        }

        return <>
            {(goalSubmissions && goalSubmissions.length !== 0) && <div className="  web-width-30 mb-auto">
                <DKLabel className="fw-m fs-m m-h-l" text="Current Goals:" />
                {goalSubmissions.map((goal, index) => renderGoal(goal, index, getCardParam(isSelfAssessment)))}
            </div>}
        </>
    }
    const getCardParam = (isSelfAssessment) =>{
        return {
            readOnly: isSelfAssessment,
            progressChange:progressChange,
            trackColor: {
                r: 253,
                g: 237,
                b: 212,
                a: 1,
            },
            fillColor: {
                r: 246,
                g: 166,
                b: 37,
                a: 1,
            },
            selfAssessment: isSelfAssessment ? 'self-goals':'evaluate',
            putComment:putComment
        }
    }
    const progressChange = (value,ind) =>{
        if(activeTab === 'evaluation') {
            let evalutation = deepClone(evaluationGoal);
            evalutation[ind].progress = value;
            setEvaluationGoal(evalutation || []);
        }
    }

    const putComment = (value,ind) => {
        if(activeTab === 'evaluation' && !Utility.isEmpty(value)) {
            let evalutation = deepClone(evaluationGoal);
            const newComment = {comment:value,createdBy: TenantService.get(TENANT.IAM_USER_ID)}
            evalutation[ind].comments = evalutation[ind].comments ? [...evalutation[ind].comments, newComment ] : [newComment];
            setEvaluationGoal(evalutation || []);
        }
    }
    

    return (
        <div className="parent-width">
            <div className="row">
                <DKLabel text={t("PERFORMANCE")} className="fs-l fw-b" />
                <div className="ml-auto d-flex">
                    <DKButton
                        title={t("CANCEL")}
                        className="bg-gray1 border-m"
                        onClick={() => {
                            RouteManager.navigateToPage(PAGE_ROUTES.ASSESSMENT_REVIEW);
                        }}
                    />
                    <DKButton
                        title={t("SAVE_AS_DRAFT")}
                        className={getDisableCSS("bg-gray1 border-green text-app ml-m", true)}
                        onClick={() => {
                            saveAssessmentReview(true);
                        }}
                    />
                    <DKButton
                        title={t("SAVE_AND_SUBMIT")}
                        className={getDisableCSS("bg-app text-white ml-m", true)}
                        onClick={() => {
                            showConfirmationMessage();
                        }}
                    />
                </div>
            </div>
            <div className="mt-xl">
                <DKLabel text={t("COMPLETE_REVIEW")} className="fs-m fw-b" />
                <DKLabel text={t("COMPLETE_REVIEW_TEXT1")} className="fs-s mt-xs text-gray" />
                <DKLabel text={t("COMPLETE_REVIEW_TEXT2")} className="fs-s mt-xs text-gray" />
            </div>
            <div className="bg-white border-radius-m pt-xl pb-m m-v-m">
                <div className="row p-h-xl">
                    <DKIcon src={ic_user} className="mr-s" />
                    <DKLabel text={assessment?.appraisee ? `${assessment?.appraisee?.firstName} ${assessment?.appraisee?.lastName}`:""} className="fw-b" />
                </div>
                <div className="row mt-l align-items-start">
                    {renderField("appraiserOverallComment", setAppraiserOverallComment, appraiserOverallComment, "45%")}
                    <div className="column align-items-center" style={{ flex: "0 0 50%", maxWidth: "45%" }}>
                        <div>
                            <DKLabel text={t("AVERAGE_RATING")} className="mb-s" />
                            <DKLabel text={averageRating} className="fw-b fs-l mb-s" />
                            <div className="star-ratings-sprite">
                                <span className="star-ratings-sprite-rating" style={{ width: `${averageRating * 20}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {getTabs()}
            <div className="row row-responsive">
                <div className="web-width-70 mb-auto">
                    <div className="bg-white border-radius-m p-v-xl mb-m">
                        {renderActiveTab()}

                    </div>
                </div>
                
                    {renderGoalSection()}
                

            </div>
        </div>
    );
}

export default AdminReview;
